/* Navbar colour overlap controller.
 *
 * Any section that opts into the "Custom Navbar Colour" override
 * carries `data-navbar-color-override` on its `.section` wrapper plus
 * an inline `--section-navbar-fore-color` CSS variable resolved by the
 * section's Liquid template (token / custom hex / accent etc.).
 *
 * This controller watches scroll + resize, and for each registered
 * section it tests whether the fixed navbar's top region is currently
 * overlapping that section's vertical bounds. While one section is
 * overlapping, body.navbar-color-active flips on and body's
 * --navbar-active-fore-color carries the section's chosen colour;
 * component-navbar.css then overrides .navbar's --fore-color /
 * --back-color / --border-color (the latter two go transparent
 * regardless of which section, per the Custom Navbar Colour spec).
 *
 * Multiple sections can be registered. The last-match-wins so that
 * a later section (further down the page) takes precedence in the
 * rare case two short sections overlap simultaneously.
 *
 * The override also hard-cuts at MAX_OVERRIDE_SCROLL px of page
 * scroll regardless of how tall the overlapping section actually is
 * — otherwise a full-height (100vh) hero keeps the override active
 * until it has scrolled entirely out of view. */

const MAX_OVERRIDE_SCROLL = 100;

class NavbarOverlapController {
  constructor() {
    this.sections = [];
    this.activeSection = null;
    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);
  }

  init() {
    this.sections = Array.from(
      document.querySelectorAll('[data-navbar-color-override]')
    );
    if (this.sections.length === 0) return;

    this.update();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  update() {
    // Measure the fixed navbar's actual rendered bottom edge rather than
    // a static --nav-main-height var: the real box also includes the
    // announcement bar above the main bar, and can be translated up by
    // CSS transform when it tucks away on scroll (component-announcement-
    // bar.css). Using the var alone ignored the announcement bar's
    // height, so the colour override dropped before the hero actually
    // scrolled out from under the header.
    const navbarEl = document.querySelector('.navbar');
    const navbarHeight = navbarEl
      ? navbarEl.getBoundingClientRect().bottom
      : 60;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    let newActive = null;
    if (scrollTop < MAX_OVERRIDE_SCROLL) {
      this.sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= navbarHeight && rect.bottom > 0) {
          newActive = section;
        }
      });
    }

    if (newActive === this.activeSection) return;
    this.activeSection = newActive;

    const body = document.body;
    if (newActive) {
      const color = getComputedStyle(newActive)
        .getPropertyValue('--section-navbar-fore-color')
        .trim();
      body.style.setProperty('--navbar-active-fore-color', color);
      body.classList.add('navbar-color-active');
    } else {
      body.classList.remove('navbar-color-active');
      body.style.removeProperty('--navbar-active-fore-color');
    }
  }
}

function start() {
  new NavbarOverlapController().init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
